/**
 * LiveNoteHistoryPanel.jsx
 * =========================
 * Persistent "Live Note History" thread that sits under the live
 * session page.  Shows the calling user's own past
 * `live_ai_in_class_notes` outputs, in reverse-chronological order,
 * each with the focus prompt the learner typed, the 6-section output,
 * the Diploks cost, and Copy + Export buttons.
 *
 * Trainer / admin pages will mount a sibling component that toggles
 * to the aggregated (`all-live-notes`) view when permission allows.
 *
 * Design:
 *   - navy/gold premium chrome
 *   - empty state: "Your live notes will appear here after you compose them."
 *   - newest note expanded by default, the rest collapsed (timestamp + focus only)
 *   - no provider / AI vendor name surfaces anywhere
 */
import { useEffect, useState, useCallback } from "react";
import {
  Sparkles, ChevronDown, ChevronUp, Copy, Download, Clock4,
  Wallet, RefreshCw, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { getMyLiveNotes, getAllLiveNotes, formatApiError } from "../../lib/liveClassApi";

function formatStamp(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso || "—"; }
}

export default function LiveNoteHistoryPanel({
  sessionId,
  /** when true, also offer the trainer/admin aggregated view toggle */
  trainerView = false,
}) {
  const [mode, setMode] = useState("mine"); // 'mine' | 'all'
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [openIds, setOpenIds] = useState(() => new Set());

  const load = useCallback(async (which) => {
    setRefreshing(true);
    try {
      const j = which === "all"
        ? await getAllLiveNotes(sessionId)
        : await getMyLiveNotes(sessionId);
      setRows(j.rows || []);
      // Auto-open the newest row.
      if (j.rows?.length) setOpenIds(new Set([j.rows[0].id]));
    } catch (e) {
      // 403 on aggregated view is expected for non-trainers; surface neutral msg.
      const status = e?.response?.status;
      if (status !== 403) toast.error(formatApiError(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [sessionId]);

  useEffect(() => { load(mode); }, [mode, load]);

  const toggle = (id) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Clipboard unavailable"); }
  };
  const exportMd = (row) => {
    const blob = new Blob([row.output?.content || ""], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `live-note-${row.id}.md`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  return (
    <section
      data-testid="live-note-history-panel"
      className="mt-6 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0A192F] to-[#0F2C50] p-5 text-white shadow-xl"
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] font-bold">
            <Sparkles size={12} /> Live Note History
          </p>
          <h2 className="mt-1 text-lg font-semibold">
            Your Ask Diplomexa Live thread
          </h2>
          <p className="mt-1 text-[11px] text-blue-200 leading-snug">
            Every live note you composed for this session, newest first.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {trainerView ? (
            <div
              data-testid="live-note-history-mode"
              className="flex items-center rounded-full bg-white/10 p-0.5"
            >
              <button
                type="button"
                data-testid="live-note-history-mode-mine"
                onClick={() => setMode("mine")}
                className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] transition ${
                  mode === "mine" ? "bg-[#D4AF37] text-[#0A192F]" : "text-white/80 hover:text-white"
                }`}
              >Mine</button>
              <button
                type="button"
                data-testid="live-note-history-mode-all"
                onClick={() => setMode("all")}
                className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] transition ${
                  mode === "all" ? "bg-[#D4AF37] text-[#0A192F]" : "text-white/80 hover:text-white"
                }`}
              >All learners</button>
            </div>
          ) : null}
          <button
            type="button"
            data-testid="live-note-history-refresh"
            onClick={() => load(mode)}
            disabled={refreshing}
            aria-label="Refresh history"
            className="rounded-full bg-white/10 hover:bg-white/20 p-2 text-white/80 hover:text-white disabled:opacity-50"
          >
            {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          </button>
        </div>
      </header>

      <div className="mt-4">
        {loading ? (
          <p className="py-8 text-center text-xs text-blue-200">Loading…</p>
        ) : rows.length === 0 ? (
          <EmptyState />
        ) : (
          <ol data-testid="live-note-history-list" className="space-y-2">
            {rows.map((row) => (
              <NoteRow
                key={row.id}
                row={row}
                open={openIds.has(row.id)}
                onToggle={() => toggle(row.id)}
                onCopy={() => copy(row.output?.content || "")}
                onExport={() => exportMd(row)}
                showAuthor={mode === "all"}
              />
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="live-note-history-empty"
      className="rounded-xl border border-dashed border-white/15 bg-black/20 px-4 py-10 text-center"
    >
      <p className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
        No live notes yet
      </p>
      <p className="mt-2 text-sm text-blue-100">
        Your live notes will appear here after you compose them.
      </p>
      <p className="mt-1 text-[11px] text-blue-200/70">
        Open the gold &ldquo;Ask Diplomexa Live&rdquo; pill while you&apos;re in the classroom.
      </p>
    </div>
  );
}

function NoteRow({ row, open, onToggle, onCopy, onExport, showAuthor }) {
  const focus = row.focus || "(no focus prompt — generic live note)";
  const content = row.output?.content || "";
  const cost = row.cost ?? 3;
  return (
    <li
      data-testid={`live-note-row-${row.id}`}
      className="rounded-xl border border-white/10 bg-black/20 overflow-hidden"
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`live-note-toggle-${row.id}`}
        className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-white/5"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-blue-200 font-bold">
            <Clock4 size={11} /> {formatStamp(row.created_at)}
            <span className="inline-flex items-center gap-1 rounded-full bg-[#D4AF37]/15 px-2 py-0.5 text-[#D4AF37]">
              <Wallet size={10} /> {cost} Diploks
            </span>
            {showAuthor && row.user_name ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-white/80">
                {row.user_name} · {row.user_role || ""}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-white/90 truncate">{focus}</p>
        </div>
        {open ? <ChevronUp size={14} className="text-white/60 mt-1" />
              : <ChevronDown size={14} className="text-white/60 mt-1" />}
      </button>
      {open ? (
        <div className="border-t border-white/10 bg-white text-[#0A192F]">
          <article
            data-testid={`live-note-content-${row.id}`}
            className="prose prose-sm max-w-none whitespace-pre-wrap px-4 py-3 text-[12px] leading-relaxed"
          >
            {content}
          </article>
          <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-3 py-2">
            <button
              type="button"
              data-testid={`live-note-copy-${row.id}`}
              onClick={onCopy}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600 hover:bg-slate-50"
            ><Copy size={11} /> Copy</button>
            <button
              type="button"
              data-testid={`live-note-export-${row.id}`}
              onClick={onExport}
              className="inline-flex items-center gap-1 rounded-md bg-[#0F4C81] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white hover:brightness-110"
            ><Download size={11} /> Export .md</button>
          </div>
        </div>
      ) : null}
    </li>
  );
}
